import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, XCircle, Lightbulb, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { LoadingSpinner } from '../components/common';
import { TaskSuggestionsSkeleton } from '../components/SkeletonLoader';
import Footer from '../components/Footer';
import { Repository } from '../types';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { analytics } from '../services/analytics/posthog';
import { TaskSuggestionList } from '../components/tasks/TaskSuggestionList';
import { useTaskSuggestions } from '../hooks/useTaskSuggestions';
import { logger } from '../utils/logger';
import {
  RepositoryHeader,
  ScoreSummary,
  AnalysisHistory,
  TaskHistory,
} from '../components/repository';
import type { AnalysisJob, TaskHistoryItem } from '../components/repository/types';
import { AutonomyPanel } from '../components/repository/AutonomyPanel';
import { ProjectsPanel } from '../components/repository/ProjectsPanel';
import { PolicyViolationsPanel } from '../components/repository/PolicyViolationsPanel';
import axios from 'axios';

export const RepositoryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useAuthenticatedApi();
  const [repository, setRepository] = useState<Repository | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startingAnalysis, setStartingAnalysis] = useState(false);
  const [refType, setRefType] = useState<'branch' | 'tag' | 'commitSha' | ''>('');
  const [refValue, setRefValue] = useState('');
  const [analysisHistory, setAnalysisHistory] = useState<AnalysisJob[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [cloneUrlCopied, setCloneUrlCopied] = useState(false);
  const [taskHistory, setTaskHistory] = useState<TaskHistoryItem[]>([]);
  const [loadingTaskHistory, setLoadingTaskHistory] = useState(false);

  const {
    suggestions: taskSuggestions,
    criticalGapsCount,
    loading: tasksLoading,
  } = useTaskSuggestions(id);

  const latestAnalysis = useMemo(() => {
    return analysisHistory.find((job) => job.status?.toLowerCase() === 'completed' && job.analysis);
  }, [analysisHistory]);

  const isUpload = repository?.platform === 'upload' || repository?.source === 'upload';

  const handleCopyCloneUrl = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCloneUrlCopied(true);
      setTimeout(() => setCloneUrlCopied(false), 2000);
    } catch {
      logger.error('[RepositoryDetail] Failed to copy clone URL');
    }
  }, []);

  // Load repository
  useEffect(() => {
    const loadRepository = async () => {
      try {
        setLoading(true);
        setError(null);

        const stateRepo = location.state?.repository as Repository | undefined;
        if (stateRepo) {
          setRepository(stateRepo);
          setLoading(false);
          return;
        }

        if (!id) {
          setError('Repository ID is required');
          setLoading(false);
          return;
        }

        const response = await api.get(`/repositories/${id}`);
        if (response.data) {
          setRepository(response.data);
        } else {
          logger.error('[RepositoryDetail] Repository not found');
          setError('Repository not found');
        }
      } catch (err: unknown) {
        logger.error('[RepositoryDetail] Failed to load repository', { error: err });

        if (axios.isAxiosError(err) && err.response?.status === 429) {
          const errorData = err.response?.data;
          if (errorData?.upgradeRequired) {
            setError(
              `Slow down there! You've hit your rate limit. ` +
                `${errorData.reason || 'Too many requests'}. ` +
                `Upgrade your plan for more runs or wait a bit.`,
            );
          } else {
            setError('Rate limit reached. Take a breather and try again in a few minutes.');
          }
        } else if (axios.isAxiosError(err) && err.response?.status === 404) {
          setError('Repository not found');
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load repository');
        }
      } finally {
        setLoading(false);
      }
    };

    loadRepository();
  }, [id, location.state, api]);

  // Load analysis history
  const loadAnalysisHistory = useCallback(async () => {
    if (!repository?.id) return;

    try {
      setLoadingHistory(true);
      setHistoryError(null);
      const response = await api.get(`/analysis/repository/${repository.id}/history`);
      setAnalysisHistory(response.data.jobs || []);
    } catch (err) {
      logger.error('[RepositoryDetail] Failed to load analysis history', { error: err });
      setAnalysisHistory([]);
      setHistoryError('Failed to load analysis history');
    } finally {
      setLoadingHistory(false);
    }
  }, [repository?.id, api]);

  useEffect(() => {
    loadAnalysisHistory();
  }, [loadAnalysisHistory]);

  // Load task history
  useEffect(() => {
    const loadTaskHistory = async () => {
      if (!repository?.id) return;

      try {
        setLoadingTaskHistory(true);
        const response = await api.get(`/tasks/repository/${repository.id}/history`);
        setTaskHistory(response.data.tasks || []);
      } catch (err) {
        logger.error('[RepositoryDetail] Failed to load task history', { error: err });
        setTaskHistory([]);
      } finally {
        setLoadingTaskHistory(false);
      }
    };

    loadTaskHistory();
  }, [repository?.id, api]);

  const handleStartAnalysis = async () => {
    if (!repository || startingAnalysis) return;

    if (isUpload) {
      analytics.analysisStarted(repository.fullName || repository.name, 'local');
      navigate('/upload', {
        state: {
          reanalyze: true,
          previousRepository: { id: repository.id, name: repository.fullName || repository.name },
        },
      });
      return;
    }

    try {
      setStartingAnalysis(true);
      setError(null);

      const response = await api.post('/analysis/repository', {
        repositoryId: repository.id,
        repositoryName: repository.fullName,
        platform: repository.platform || repository.provider || 'github',
        repositoryUrl: repository.url,
        cloneUrl: repository.cloneUrl || repository.url,
        refType: refType || undefined,
        refValue: refValue.trim() || undefined,
      });

      analytics.analysisStarted(
        repository.url || repository.fullName,
        (repository.platform || repository.provider || 'local') as 'github' | 'gitlab' | 'local',
      );

      navigate(`/jobs/${response.data.jobId}`, { state: { repository } });
    } catch (err: unknown) {
      logger.error('[RepositoryDetail] Failed to start analysis', { error: err });

      if (axios.isAxiosError(err) && err.response?.status === 429) {
        const errorData = err.response?.data;
        if (errorData?.upgradeRequired) {
          setError(
            `Rate limit exceeded: ${errorData.reason || 'You have reached your API request limit'}. ` +
              `Current usage: ${errorData.usage?.current || 0}/${errorData.usage?.limit || 0}. ` +
              `Please upgrade your plan to continue.`,
          );
        } else {
          setError('Rate limit exceeded. Please try again later.');
        }
      } else {
        const code = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
        // Provider connection/token errors are surfaced by the global reconnect
        // modal, so don't replace the page with a raw error code for those.
        const handledByModal =
          code === 'connection_required' ||
          code === 'token_expired' ||
          code === 'token_validation_failed';
        if (!handledByModal) {
          setError(code || 'Failed to start analysis. Please try again.');
        }
      }
    } finally {
      setStartingAnalysis(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error / not-found state
  if (error || !repository) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar pageTitle="Repository" showDivider={true} />
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8 pt-24">
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h2
              className="text-xl font-semibold mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {error?.includes('not found') ? 'Repository Not Found' : 'Something Went Wrong'}
            </h2>
            <p className="mb-8" style={{ color: 'var(--color-text-secondary)' }}>
              {error || "The repository you're looking for doesn't exist."}
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col px-4 sm:px-6 lg:px-8">
      <Navbar pageTitle={repository.fullName || repository.name} showDivider={true} />

      <main className="flex-1 max-w-7xl mx-auto py-8 pt-24 w-full space-y-6">
        {/* Breadcrumb */}
        <button
          onClick={() => {
            const provider = repository?.platform === 'gitlab' ? 'gitlab' : 'github';
            navigate(`/repositories?provider=${provider}`);
          }}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to repositories
        </button>

        {/* Repository Header (bg-white dark:bg-gray-800) */}
        <RepositoryHeader
          repository={repository}
          isUpload={isUpload}
          startingAnalysis={startingAnalysis}
          cloneUrlCopied={cloneUrlCopied}
          onStartAnalysis={handleStartAnalysis}
          onCopyCloneUrl={handleCopyCloneUrl}
          refType={refType}
          refValue={refValue}
          onRefTypeChange={setRefType}
          onRefValueChange={setRefValue}
        />

        {/* Score Summary (hero SDLC card + stat cards) */}
        {latestAnalysis && (
          <ScoreSummary latestAnalysis={latestAnalysis} actionCount={taskSuggestions.length} />
        )}

        {/* Recommended Actions */}
        {latestAnalysis && (
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
                <Lightbulb className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              </div>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Recommended Actions
              </h2>
            </div>

            {tasksLoading ? (
              <TaskSuggestionsSkeleton />
            ) : taskSuggestions.length > 0 ? (
              <TaskSuggestionList
                suggestions={taskSuggestions}
                repositoryId={repository.id}
                containerId={latestAnalysis?.containerId}
              />
            ) : criticalGapsCount > 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-amber-200 dark:border-amber-900/40 px-6 py-12 flex flex-col items-center text-center gap-3">
                <div className="p-3 rounded-full bg-amber-50 dark:bg-amber-900/20">
                  <AlertTriangle className="w-7 h-7 text-amber-500 dark:text-amber-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  No automated actions for your critical issues
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                  This scan surfaced {criticalGapsCount}{' '}
                  {criticalGapsCount === 1 ? 'issue' : 'issues'} we don't yet have a maintenance
                  task for. Review the SDLC Report for manual remediation steps.
                </p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-6 py-12 flex flex-col items-center text-center gap-3">
                <div className="p-3 rounded-full bg-teal-50 dark:bg-teal-900/20">
                  <CheckCircle2 className="w-7 h-7 text-teal-500 dark:text-teal-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  All caught up
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                  We didn't find any maintenance tasks for this project. Run another analysis
                  anytime to check for new recommendations.
                </p>
              </div>
            )}
          </section>
        )}

        {/* Analysis History */}
        <AnalysisHistory
          history={analysisHistory}
          loading={loadingHistory}
          error={historyError}
          startingAnalysis={startingAnalysis}
          isUpload={isUpload}
          onStartAnalysis={handleStartAnalysis}
          onRetry={loadAnalysisHistory}
        />

        {/* Past Actions */}
        <TaskHistory history={taskHistory} loading={loadingTaskHistory} />

        {/* Fleet policy violations (governance enforcement) */}
        <PolicyViolationsPanel repositoryId={repository.id} />

        {/* Monorepo project decomposition (key projects) */}
        <ProjectsPanel repositoryId={repository.id} />

        {/* Autonomous maintenance settings + activity */}
        <AutonomyPanel repositoryId={repository.id} />
      </main>

      <Footer />
    </div>
  );
};
